import { CommandDefinition, defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Component, Convention, ObjectManager } from '../../../concepts/objects';
import { Resolver } from '../../Container';
import { EditorSession } from '../EditorSession';
import { Physical } from '../entities/Physical';
import { Selection } from '../Selection';

const resolvePhyiscalByIdOrName = (cc: Resolver, idOrName: string): Physical => {
    const objects = cc.call(ObjectManager);
    if (idOrName.match(/\w+\[.+\]/)) {
        const [type, id] = idOrName.slice(0, -1).split('[');
        const typeDef = Component.describe(type);
        if (typeDef) {
            const inst = objects.getMaybe(id, typeDef.type);
            if (inst?.is(Physical)) {
                return inst as Physical;
            }
        }
    }
    if (idOrName.startsWith('@')) {
        const n = idOrName.slice(1);
        const o = objects.find(Physical, x => x.name === n);
        if (!!o) return o;
    }
    return objects.getMaybe(idOrName)?.as(Physical);
};

export const resolvePhysicals = <T extends Physical>(cc: Resolver, input: any, toc?: Convention<T>): T[] => {

    // If input is a string, split on commands to support csv
    if (typeof (input) === 'string') {
        input = input.split(',').map(x => x.trim()).filter(x => !!x);
    }
    // If input is a single component, wrap in array
    if (input instanceof Component) {
        input = [input];
    }
    // If input is array of strings, convert ids or names into elements
    if (Array.isArray(input) && input.some(x => typeof (x) === 'string')) {
        input = input.map(x => typeof (x) === 'string' ? resolvePhyiscalByIdOrName(cc, x) : x);
    }
    // Filter input for components of the correct type
    return input.filter(x => {
        if (!(x instanceof Component)) return false;
        if (!x.alive) return false;
        if (!x.mounted()) return false;
        if (!!toc && !x.is(toc)) return false;
        return true;
    });
};

export const resolvePhysical = <T extends Physical>(cc: Resolver, input: any, toc?: Convention<T>): T => {
    if (!Array.isArray(input)) {
        input = [input];
    }
    return resolvePhysicals(cc, input, toc)[0] || null;
};

// eslint-disable-next-line max-len
export const resolveSubjects = <T extends Physical>(cc: Resolver, targets?: string | string[], toc?: Convention<T>): T[] => {

    const list = Array.isArray(targets) ? targets : (!!targets ? [targets] : []);
    const phys = [] as Physical[];

    const objects = cc.call(ObjectManager);

    for (const entry of list) {
        if (entry.startsWith('@')) {
            const n = entry.slice(1);
            const o = objects.find(Physical, x => x.name === n);
            if (!o) throw new Error(`Named object not found: ${n}`);
            phys.push(o);
        }
        else {
            phys.push(objects.getMaybe(entry, Physical));
        }
    }

    const safe = (!!phys.length ? phys : cc.call(EditorSession).get(Selection))
        .filter(x => x?.alive && x?.mounted() && x?.is(toc ?? Physical));

    return safe as unknown as T[];
};

export const resolveSubject = <T extends Physical>(r: Resolver, target?: string, toc?: Convention<T>) => {
    return resolveSubjects(r, !!target ? [target] : null, toc)[0] ?? null;
};

export const composeCommands = (name: string, label: string, ...composition: [CommandDefinition<unknown>, unknown[]?][]) => {
    return defineCommand(
        name,
        label,
        r => {
            const objects = r.call(ObjectManager);
            const dispatch = r.call(CommandDispatcher);
            objects.update(u => {
                u.set('key', name);
                for (const [cmd, args] of composition) {
                    dispatch(cmd, args);
                }
            });
        }
    );
};

